import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { Plus, Upload, FileText, Trash2, Check, X, AlertOctagon, RefreshCw, Loader2, Pencil, Landmark } from "lucide-react";
import { toast } from "sonner";
import { classifyFeeKind } from "@/lib/fees";
import EditTransactionModal from "@/components/EditTransactionModal";
import DateInput from "@/components/DateInput";

const CATEGORIES = [
  "Revenue", "Software", "Rent", "Utilities", "Meals", "Travel",
  "Supplies", "Marketing", "Insurance", "Contractors", "Education",
  "Equipment", "Taxes", "Debt Payment", "Other",
];

function NewTransactionForm({ onCreated, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, description: "", amount: "", category: "Other",
    type: "expense", is_tax_deductible: false, notes: "",
    bank_account_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bank-accounts");
        setAccounts(data.accounts || []);
        const primary = (data.accounts || []).find((a) => a.is_primary);
        if (primary) setForm((f) => ({ ...f, bank_account_id: primary.id }));
      } catch {
        /* silent — accounts feature is optional */
      }
    })();
  }, []);

  const feeKind = form.type === "expense" ? classifyFeeKind(form.description) : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amt = parseFloat(form.amount);
      const signed = form.type === "expense" ? -Math.abs(amt) : Math.abs(amt);
      const payload = { ...form, amount: signed };
      if (!payload.bank_account_id) delete payload.bank_account_id;
      const { data } = await api.post("/transactions", payload);
      toast.success("Transaction added");
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-surface p-6 space-y-4 mb-6" data-testid="new-transaction-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="text-sm">
          <div className="font-medium mb-1.5">Date</div>
          <DateInput
            required
            value={form.date}
            onChange={(iso) => setForm({ ...form, date: iso })}
            data-testid="txn-date-input"
          />
        </label>
        <label className="text-sm md:col-span-2">
          <div className="font-medium mb-1.5">Description</div>
          <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="txn-description-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none" />
          {feeKind && (
            <div className="mt-2 flex items-start gap-2 text-xs bg-ochre/10 border border-ochre/25 rounded-md px-3 py-2" data-testid="fee-warning">
              <AlertOctagon className="w-3.5 h-3.5 text-ochre mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <span className="font-medium text-ink">
                  {feeKind === "nsf" ? "Looks like an NSF / overdraft charge." : "Looks like a bank fee."}
                </span>
                <span className="text-ink-light ml-1">
                  Many of these are refundable — call your bank for a courtesy reversal. We'll track this on the Statements page.
                </span>
              </div>
            </div>
          )}
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Type</div>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="txn-type-select"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none">
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Amount</div>
          <input type="number" step="0.01" min="0" required value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid="txn-amount-input"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none num" />
        </label>
        <label className="text-sm">
          <div className="font-medium mb-1.5">Category</div>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="txn-category-select"
            className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {accounts.length > 0 && (
          <label className="text-sm md:col-span-2">
            <div className="font-medium mb-1.5">Bank account <span className="text-ink-lighter font-normal">(optional)</span></div>
            <select
              value={form.bank_account_id}
              onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
              data-testid="txn-bank-account-select"
              className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
            >
              <option value="">— Unassigned —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.mask ? ` · ••${a.mask}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm" data-testid="txn-deductible-label">
        <input type="checkbox" checked={form.is_tax_deductible} onChange={(e) => setForm({ ...form, is_tax_deductible: e.target.checked })}
          data-testid="txn-deductible-checkbox" className="accent-forest" />
        <span>Tax deductible</span>
      </label>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-txn">Cancel</button>
        <button type="submit" disabled={saving} className="btn-forest" data-testid="save-txn-button">
          {saving ? "Saving…" : "Save transaction"}
        </button>
      </div>
    </form>
  );
}

function ParsePreview({ items, onConfirm, onCancel }) {
  // Ensure each row has a stable client-side id so React keys don't reorder if the user edits rows.
  const [rows, setRows] = useState(() =>
    items.map((r, i) => ({ ...r, _rowId: r._rowId ?? `${r.date}-${r.description}-${r.amount}-${i}` }))
  );
  const update = (i, k, v) => {
    const r = [...rows]; r[i] = { ...r[i], [k]: v }; setRows(r);
  };
  const submit = async () => {
    const payload = rows.map((r) => ({
      date: r.date, description: r.description, amount: r.amount,
      category: r.suggested_category || "Other", type: r.type,
      is_tax_deductible: !!r.is_tax_deductible, notes: null,
    }));
    try {
      const { data } = await api.post("/transactions/bulk", payload);
      toast.success(`${data.inserted} transactions added`);
      onConfirm();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="card-surface p-6 mb-6" data-testid="parse-preview">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1">Bank statement preview</div>
          <h3 className="font-heading text-2xl font-medium">{rows.length} transactions detected</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary inline-flex items-center gap-1.5" data-testid="cancel-parse"><X className="w-4 h-4" /> Cancel</button>
          <button onClick={submit} className="btn-forest inline-flex items-center gap-1.5" data-testid="confirm-parse"><Check className="w-4 h-4" /> Add all</button>
        </div>
      </div>
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink-lighter border-b border-border">
              <th className="py-2.5 pr-3">Date</th>
              <th className="py-2.5 pr-3">Description</th>
              <th className="py-2.5 pr-3">Category</th>
              <th className="py-2.5 pr-3">Deduct?</th>
              <th className="py-2.5 pr-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r._rowId} className="border-b border-border last:border-0" data-testid={`parse-row-${i}`}>
                <td className="py-2 pr-3 num text-xs">{fmtDate(r.date)}</td>
                <td className="py-2 pr-3">{r.description}</td>
                <td className="py-2 pr-3">
                  <select value={r.suggested_category || "Other"} onChange={(e) => update(i, "suggested_category", e.target.value)}
                    className="bg-white border border-border rounded px-2 py-1 text-xs">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <input type="checkbox" checked={!!r.is_tax_deductible} onChange={(e) => update(i, "is_tax_deductible", e.target.checked)} className="accent-forest" />
                </td>
                <td className={`py-2 pr-3 num text-right ${r.amount < 0 ? "text-ink" : "text-forest"}`}>{fmtMoney(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Transactions() {
  const nav = useNavigate();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [parsedItems, setParsedItems] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [qboStatus, setQboStatus] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | synced | unsynced
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // currently-editing txn
  const [accounts, setAccounts] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const csvRef = useRef();
  const pdfRef = useRef();

  const load = async () => {
    setLoading(true);
    const [{ data: t }, { data: qs }, ba] = await Promise.all([
      api.get("/transactions"),
      api.get("/qbo/status").catch(() => ({ data: { configured: false, connected: false } })),
      api.get("/bank-accounts").catch(() => ({ data: { accounts: [] } })),
    ]);
    setTxns(t);
    setQboStatus(qs);
    setAccounts(ba.data?.accounts || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const accountsById = React.useMemo(() => {
    const m = {};
    for (const a of accounts) m[a.id] = a;
    return m;
  }, [accounts]);
  const untaggedCount = txns.filter((t) => !t.bank_account_id).length;

  const pushToQBO = async (idsOverride) => {
    if (!qboStatus?.connected) {
      toast.info("Connect to QuickBooks first");
      nav("/app/integrations");
      return;
    }
    setPushing(true);
    try {
      const body = idsOverride && idsOverride.length > 0 ? { transaction_ids: idsOverride } : {};
      const { data } = await api.post("/qbo/sync", body);
      if (data.synced === 0 && data.skipped === 0) {
        toast.info("Nothing to sync — all transactions already pushed.");
      } else {
        toast.success(`Pushed ${data.synced} to QuickBooks · skipped ${data.skipped}${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      }
      if (data.errors?.length) {
        toast.error(data.errors[0].reason, { duration: 6000 });
        // Auto-focus the user on the rows that still need attention.
        setFilter("unsynced");
      }
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Push to QuickBooks failed");
    } finally {
      setPushing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const visibleIds = visibleTxns.filter((t) => !t.qbo_synced_at).map((t) => t.id);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  };

  const uploadCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/transactions/import/csv", fd);
      toast.success(`Imported ${data.imported} transactions`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadPdf = async (file) => {
    setParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/transactions/parse/pdf", fd);
      if (data.length === 0) {
        toast.info("Couldn't auto-detect transactions in this PDF. Try a CSV export from your bank.");
      } else {
        setParsedItems(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setParsing(false);
    }
  };

  const remove = async (id) => {
    const t = txns.find((x) => x.id === id);
    const label = t ? `“${t.description}” (${fmtMoney(t.amount)})` : "this transaction";
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await api.delete(`/transactions/${id}`);
      setTxns(txns.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error("Delete failed"); }
  };

  const onTxnUpdated = (updated) => {
    setTxns((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  };

  const totalIncome = txns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const totalExpense = txns.filter((t) => t.type === "expense").reduce((s, t) => s + (-t.amount), 0);
  const syncedCount = txns.filter((t) => t.qbo_synced_at).length;
  const unsyncedCount = txns.length - syncedCount;
  const visibleTxns =
    filter === "synced" ? txns.filter((t) => t.qbo_synced_at)
    : filter === "unsynced" ? txns.filter((t) => !t.qbo_synced_at)
    : txns;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="transactions-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Transactions</div>
          <h1 className="font-heading text-4xl font-medium tracking-tight">Every dollar, accounted for.</h1>
          <p className="text-ink-light mt-2">{txns.length} total · {fmtMoney(totalIncome)} in · {fmtMoney(totalExpense)} out</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && uploadCsv(e.target.files[0])} data-testid="csv-file-input" />
          <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadPdf(e.target.files[0])} data-testid="pdf-file-input" />
          <button onClick={() => csvRef.current?.click()} className="btn-secondary inline-flex items-center gap-2" data-testid="import-csv-button">
            <Upload className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => pdfRef.current?.click()} disabled={parsing} className="btn-secondary inline-flex items-center gap-2" data-testid="import-pdf-button">
            <FileText className="w-4 h-4" /> {parsing ? "Parsing…" : "Bank PDF"}
          </button>
          {accounts.length > 0 && untaggedCount > 0 && (
            <button
              onClick={() => setBulkAssignOpen(true)}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="bulk-assign-button"
              title={`Tag the ${untaggedCount} un-tagged transaction${untaggedCount === 1 ? "" : "s"} in one click`}
            >
              <Landmark className="w-4 h-4" /> Tag {untaggedCount} untagged
            </button>
          )}
          <button
            onClick={pushToQBO}
            disabled={pushing || txns.length === 0}
            data-testid="push-to-qbo-button"
            title={qboStatus?.connected ? "Push un-synced transactions to your connected QuickBooks company" : "Connect QuickBooks first"}
            className="btn-secondary inline-flex items-center gap-2"
          >
            {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {pushing ? "Pushing…" : "Push to QuickBooks"}
            {qboStatus?.connected === false && (
              <span className="text-[10px] text-ochre uppercase tracking-wide">not connected</span>
            )}
          </button>
          <button onClick={() => setShowNew(!showNew)} className="btn-forest inline-flex items-center gap-2" data-testid="add-transaction-button">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {showNew && <NewTransactionForm onCreated={() => load()} onClose={() => setShowNew(false)} />}
      {parsedItems && <ParsePreview items={parsedItems} onConfirm={() => { setParsedItems(null); load(); }} onCancel={() => setParsedItems(null)} />}

      {qboStatus?.connected && txns.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="txn-filter-chips">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { v: "all",      label: "All",       count: txns.length },
              { v: "synced",   label: "Synced",    count: syncedCount },
              { v: "unsynced", label: "Un-synced", count: unsyncedCount },
            ].map((c) => (
              <button
                key={c.v}
                onClick={() => setFilter(c.v)}
                data-testid={`txn-filter-${c.v}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  filter === c.v
                    ? "bg-forest text-white border-forest"
                    : "bg-white text-ink-light border-border hover:text-ink hover:border-ink/30"
                }`}
              >
                {c.label}
                <span className={`num text-[10px] ${filter === c.v ? "text-white/80" : "text-ink-lighter"}`}>{c.count}</span>
              </button>
            ))}
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="bulk-push-bar">
              <span className="text-xs text-ink-light">{selected.size} selected</span>
              <button
                onClick={() => setSelected(new Set())}
                data-testid="clear-selection-button"
                className="text-xs text-ink-lighter hover:text-ink"
              >
                Clear
              </button>
              <button
                onClick={() => pushToQBO(Array.from(selected))}
                disabled={pushing}
                data-testid="push-selected-button"
                className="btn-forest text-xs inline-flex items-center gap-1.5"
              >
                {pushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Push {selected.size} to QuickBooks
              </button>
            </div>
          )}
        </div>
      )}

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-ink-lighter">Loading…</div>
        ) : txns.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <div className="text-ink-lighter mb-3">No transactions yet</div>
            <button onClick={() => setShowNew(true)} className="btn-forest">Add your first</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-lighter border-b border-border">
                  {qboStatus?.connected && (
                    <th className="pl-4 pr-1 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid="select-all-checkbox"
                        checked={visibleTxns.length > 0 && visibleTxns.filter((t) => !t.qbo_synced_at).every((t) => selected.has(t.id)) && visibleTxns.some((t) => !t.qbo_synced_at)}
                        onChange={toggleSelectAllVisible}
                        aria-label="Select all un-synced visible rows"
                        className="rounded border-ink-lighter"
                      />
                    </th>
                  )}
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Deductible</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visibleTxns.length === 0 ? (
                  <tr>
                    <td colSpan={qboStatus?.connected ? 7 : 6} className="px-5 py-10 text-center text-sm text-ink-lighter" data-testid="txn-filter-empty">
                      {filter === "synced"
                        ? "No transactions have been synced to QuickBooks yet."
                        : filter === "unsynced"
                        ? "Everything is synced — nothing pending. ✓"
                        : "No transactions."}
                    </td>
                  </tr>
                ) : null}
                {visibleTxns.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-paper/60" data-testid={`txn-row-${t.id}`}>
                    {qboStatus?.connected && (
                      <td className="pl-4 pr-1 py-3">
                        {t.qbo_synced_at ? (
                          <span className="block w-4 h-4" aria-hidden />
                        ) : (
                          <input
                            type="checkbox"
                            data-testid={`select-txn-${t.id}`}
                            checked={selected.has(t.id)}
                            onChange={() => toggleSelect(t.id)}
                            aria-label={`Select ${t.description}`}
                            className="rounded border-ink-lighter"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3 num text-xs whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink">{t.description}</span>
                        {t.bank_account_id && accountsById[t.bank_account_id] && (
                          <span
                            data-testid={`account-pill-${t.id}`}
                            title={`Tagged to ${accountsById[t.bank_account_id].name}`}
                            className="inline-flex items-center gap-1 text-[10px] font-medium bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded"
                          >
                            {accountsById[t.bank_account_id].name}
                            {accountsById[t.bank_account_id].mask && ` ··${accountsById[t.bank_account_id].mask}`}
                          </span>
                        )}
                        {t.qbo_synced_at && (
                          <span
                            data-testid={`qbo-synced-${t.id}`}
                            title={`Synced to QuickBooks on ${fmtDateTime(t.qbo_synced_at)}`}
                            className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide bg-forest/10 text-forest px-1.5 py-0.5 rounded"
                          >
                            <Check className="w-3 h-3" strokeWidth={2.5} /> QB
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3"><span className="inline-block px-2 py-0.5 rounded-full bg-forest/8 text-forest text-xs">{t.category}</span></td>
                    <td className="px-5 py-3">
                      {t.is_tax_deductible ? <Check className="w-4 h-4 text-forest" /> : <span className="text-ink-lighter">—</span>}
                    </td>
                    <td className={`px-5 py-3 num text-right ${t.amount < 0 ? "text-ink" : "text-forest font-medium"}`}>{fmtMoney(t.amount)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setEditing(t)}
                          className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5 transition-colors"
                          data-testid={`edit-txn-${t.id}`}
                          title="Edit transaction"
                          aria-label={`Edit ${t.description}`}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          onClick={() => remove(t.id)}
                          className="p-1.5 rounded-md text-ink-lighter hover:text-destructive hover:bg-destructive/5 transition-colors"
                          data-testid={`delete-txn-${t.id}`}
                          title="Delete transaction"
                          aria-label={`Delete ${t.description}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <EditTransactionModal
          txn={editing}
          onClose={() => setEditing(null)}
          onSaved={onTxnUpdated}
        />
      )}
      {bulkAssignOpen && (
        <BulkAssignModal
          count={untaggedCount}
          accounts={accounts}
          onClose={() => setBulkAssignOpen(false)}
          onDone={() => { setBulkAssignOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BulkAssignModal({ count, accounts, onClose, onDone }) {
  const primary = accounts.find((a) => a.is_primary);
  const [accountId, setAccountId] = React.useState(primary?.id || accounts[0]?.id || "");
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accountId) return;
    setSaving(true);
    try {
      const { data } = await api.post("/transactions/bulk-assign-account", {
        bank_account_id: accountId,
        scope: "untagged",
      });
      const acc = accounts.find((a) => a.id === accountId);
      toast.success(`Tagged ${data.modified} transaction${data.modified === 1 ? "" : "s"} to ${acc?.name || "account"}.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk assign failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="bulk-assign-modal"
    >
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Bulk-assign</div>
            <div className="font-heading text-xl font-medium">
              Tag {count} untagged transaction{count === 1 ? "" : "s"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-ink-lighter hover:text-ink hover:bg-paper" data-testid="bulk-assign-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-ink-light mb-4">
          Every transaction with no bank account yet will be moved into the account you pick below.
          Balance calculations pick this up instantly.
        </p>
        <label className="block mb-6">
          <span className="block text-xs text-ink-light mb-1">Assign to</span>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            data-testid="bulk-assign-account-select"
            className="w-full bg-white border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-forest"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.mask ? ` · ··${a.mask}` : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink" data-testid="bulk-assign-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving || !accountId} className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="bulk-assign-confirm">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Tagging…" : `Tag ${count} transactions`}
          </button>
        </div>
      </form>
    </div>
  );
}
